<template>
  <div
    :class="[
      'input-custom-container',
      'is-button-toggle-password',
      {
        'is-error': props.isError,
        'is-disabled': props.isDisabled,
      },
      props.className,
    ]"
    :style="[props.inlineStyles]"
  >
    <div v-if="!props.isHideLabel">
      <div v-if="focus || props.value" class="label-custom">
        {{ props.label }}
      </div>
    </div>
    <input
      ref="myInput"
      :class="['input-custom', { 'is-icon-clear': props.value }, props.inputClassname]"
      :type="props.type"
      :placeholder="props.label"
      :name="props.name"
      :value="props.value"
      @input="updateValue($event)"
      @keypress="onKeypress($event)"
      @focus="
        ($event) => {
          focus = true;
          emit('focus', $event);
        }
      "
      @blur="
        ($event) => {
          focus = false;
          emit('blur', $event);
        }
      "
    />
    <div v-if="props.onClickClear && props.value && !props.isDisabled" class="input-custom-section is-button-toggle-password" @click="onClear">
      <img src="/images/icons/ic-clear.svg" alt="Icon" />
    </div>
    <div v-if="!!props.iconSuffix" class="input-custom-section is-icon-suffix" @click="onClickSuffix">
      <img :src="props.iconSuffix" alt="Icon" />
    </div>
  </div>
</template>

<script setup>
const componentName = "Input";

// Props types: String, Number, Boolean, Array, Object, Function, Promise
const props = defineProps({
  className: {
    type: String,
  },
  inlineStyles: {
    type: Object,
    default: function () {
      return {
        // Style
      };
    },
  },
  type: {
    type: String,
  },
  isError: {
    type: Boolean,
  },
  isDisabled: {
    type: Boolean,
  },
  isNumber: {
    type: Boolean,
  },
  name: {
    type: String,
  },
  value: {
    type: [String, Number],
    default: "",
  },
  label: {
    type: String,
    default: "label",
  },
  onChangeInput: {
    type: Function,
  },
  validateInput: {
    type: Function,
    default: function (value) {
      return value;
    },
  },
  onClickClear: {
    type: Function,
  },
  inputClassname: {
    type: String,
  },
  iconSuffix: {
    type: String,
  },
  onClickSuffix: {
    type: Function,
  },
  onKeypress: {
    type: Function,
    default: () => {}
  },
  isHideLabel: {
    type: Boolean,
  },
});

const focus = ref(false);
const myInput = ref(null);

const emit = defineEmits(["input", "focus", "blur"]);

const updateValue = (ev) => {
  const value = props.validateInput(ev.target.value);
  emit("input", value);
  ev.target.value = value;
  if (props.onChangeInput) {
    props.onChangeInput(ev.target.value);
  }
};
const onKeypress = (evt) => {
  evt = evt ? evt : window.event;
  const charCode = evt.which ? evt.which : evt.keyCode;
  if (props.isNumber) {
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      evt.preventDefault();
    }
  }
  props.onKeypress(evt);
  return true;
};
const onClear = () => {
  if (props.onClickClear) {
    props.onClickClear();
  }
  myInput.value.focus();
};
</script>

<style lang="scss">
.input-custom-container {
  /* Parent style   ------------------------------ */
  position: relative;
  display: inline-block;
  margin-top: 11px;

  /* Children style ------------------------------ */
  .input-custom {
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-md-font-size;
    font-weight: 400;
    line-height: $semantic-text-body-md-line-height-leading-none;
    text-align: left;

    width: 240px;
    height: $semantic-dimension-cta-height-md;
    padding: 0px $px-3d5 0px $px-3d5;
    gap: $space-x-2;
    border-radius: $semantic-dimension-cta-rounded-md;
    border: $semantic-dimension-cta-border-pixel;
     
    background-color: $semantic-color-form-background;
    border: 1px solid $semantic-color-form-border;
    &.is-icon-clear {
      padding: 0px $px-8 0px $px-3d5;
    }
  }

  input:focus {
    box-shadow: 0 0 0 0 $semantic-color-form-background, 0 0 0 1px $semantic-color-form-border-focus-checked; /* ring-offset-width, ring-width */
    border: 1px solid $semantic-color-form-border-focus-checked;
    outline: none;
  }

  input:focus::placeholder {
    color: $semantic-color-form-background;
  }

  .input-custom-section {
    &.is-button-toggle-password {
      @include button-transition;
      position: absolute;
      z-index: 1;
      top: 7px;
      right: 10px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      width: 24px;
      height: 24px;
    }

    &.is-icon-suffix {
      @include button-transition;
      position: absolute;
      z-index: 1;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      display: flex;
      justify-content: flex-end;
      align-items: center;
      width: 24px;
      height: 24px;
    }
  }

  .label-custom {
    max-width: 200px;
    height: 12px;
    padding: 0px $px-1 0px $px-1;
    border: 0px 0px 3px 0px;
    gap: 0px;
     
    position: absolute;
    z-index: 1;
    top: -11px;
    left: 10px;
    //styleName: semantic/body/xs-leading-none;
    font-family: $typography-font-family-primary;
    font-size: $semantic-text-body-xs-font-size;
    font-weight: 400;
    line-height: $semantic-text-body-xs-line-height-leading-none;
    text-align: left;
    color: #6b7280;
    background-color: $semantic-color-form-background;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* State */
  &.is-error {
    .input-custom {
      border-color: $semantic-color-status-error-border-validation;
    }
    input:focus {
      box-shadow: 0 0 0 0 $semantic-color-form-background, 0 0 0 1px $semantic-color-status-error-border-validation; /* ring-offset-width, ring-width */
      border: 1px solid $semantic-color-status-error-border-validation;
      outline: none;
    }
  }

  &.is-disabled {
    .input-custom {
      pointer-events: none;
      background-color: $semantic-color-status-disabled-background;
      color: $semantic-color-text-form-placeholder;
    }
  }
}
</style>
